import { InjectionToken } from '@angular/core';

/**
 * Describes the viewport size
 */
export class ScreenSize {
    /**
     * The width in pixels of the viewport
     */
    width: number;

    /**
     * The height in pixels of the viewport
     */
    height: number;
}

export interface BreakpointConfig {
    /**
     * The minimum width in pixels of the breakpoint
     *
     * Optional
     */
    minWidth?: number;

    /**
     * The maximum width in pixels of the breakpoint
     *
     * Optional
     */
    maxWidth?: number;
}

/**
 * Describes a breakpoint
 */
export class Breakpoint {
    /**
     * The reference name to this specific breakpoint
     */
    name: string;

    /**
     * The media query to trigger this breakpoint
     */
    mediaQuery: string;

    /**
     * Generate a `Breakpoint` with a name and a width configuration, which resolved
     * into a media query.
     * @param name
     * @param config
     */
    static fromWidth(name: string, config: BreakpointConfig): Breakpoint {
        const queries: string[] = [];

        if (config.minWidth) {
            queries.push('(min-width: ' + config.minWidth + 'px)');
        }

        if (config.maxWidth) {
            queries.push('(max-width: ' + config.maxWidth + 'px)');
        }

        const model: Breakpoint = {
            name,
            mediaQuery: queries.join(' AND ')
        };

        return model;
    }

    /**
     * Generate a `Breakpoint` with a name and a media query.
     * @param name
     * @param mediaQuery
     */
    static fromQuery(name: string, mediaQuery: string): Breakpoint {
        return {
            name,
            mediaQuery
        };
    }
}

/**
 * Describes a list of breakpoints
 */
export type Breakpoints = Breakpoint[];

/**
 * A set of default breakpoints: mobile, tablet and desktop
 */
export const defaultBreakpoints: Breakpoints = [
    Breakpoint.fromWidth('mobile', { maxWidth: 567 }),
    Breakpoint.fromWidth('tablet', { minWidth: 568, maxWidth: 1024 }),
    Breakpoint.fromWidth('desktop', { minWidth: 1025 })
];

/**
 * Extra options to support server-side mode
 */
export class SsrOptions {
    /**
     * Indicates the default size to use when in server-side mode
     */
    defaultSize: ScreenSize = {
        width: 375,
        height: 667
    };
}

/**
 * Token to provide `Breakpoints` to the `BreakpointService`
 */
export const BREAKPOINTS: InjectionToken<Breakpoints> = new InjectionToken<Breakpoints>('BREAKPOINTS');

/**
 * Token to provide `SsrOptions` to the `BreakpointService`
 */
export const SSR_OPTIONS: InjectionToken<SsrOptions> = new InjectionToken<SsrOptions>('SSR_OPTIONS');
